import { useTranslation } from 'react-i18next';
import { ContentfulMedia } from '@wbk/contentful';

import { ChevronIcon } from '@wbk/svg';
import { Button, Typography } from '@wbk/ui';
import { twMerge } from 'tailwind-merge';
import { CurrencyLabel } from '@/components/common/CurrencyLabel';
import type { ContentfulMedia as ContentfulMediaType } from '@wbk/contentful/api';

interface EventCardProps {
  venueName: string;
  zoneLogo?: ContentfulMediaType;
  eventDate?: string;
  priceFrom: number | undefined | null;
  currency?: string;
  image: ContentfulMediaType;

  buttonText?: string;
  onButtonClick?: () => void;
  className?: string;
  buttonContainerClassName?: string;
  eventType?: string;
}

const EventCard = ({
  venueName,
  eventDate,
  zoneLogo,
  priceFrom,
  image,
  onButtonClick,
  className,
  buttonContainerClassName = '',
  buttonText,
  eventType,
}: EventCardProps) => {
  const { t } = useTranslation('common');
  return (
    <div
      className={twMerge(
        'group relative w-full overflow-hidden rounded-2xl md:rounded-3xl dark:bg-black dark:text-[#F4F4F5]',
        className
      )}
    >
      {/* Background Image */}
      <div className='group relative h-80'>
        <ContentfulMedia
          media={image}
          className='h-full w-full object-cover object-top transition-transform duration-300 ease-in-out group-hover:scale-110'
        />
        <div className='absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/40 transition-all duration-300 ease-in-out group-hover:opacity-0' />

        {zoneLogo && (
          <div className='absolute left-2 top-2 sm:left-4 sm:top-4'>
            <div className='rounded-lg bg-black/80 px-4 py-3 backdrop-blur-sm'>
              <ContentfulMedia
                media={zoneLogo}
                draggable={false}
                className='h-7 w-6 object-contain md:h-14 md:w-12'
              />
            </div>
          </div>
        )}

        <div className='absolute inset-x-0 bottom-0 flex flex-col items-start gap-1 p-2 backdrop-blur-[1px] sm:p-4'>
          <Typography className='text-body-S sm:text-body-M text-start font-semibold ltr:leading-[100%]'>
            {venueName}
          </Typography>

          {eventDate && (
            <Typography variant='body-XS' className='line-clamp-1 text-start'>
              {eventDate}
            </Typography>
          )}

          <div
            className={`flex w-full flex-col flex-wrap items-start justify-between gap-2 md:flex-row md:items-center ${buttonContainerClassName}`}
          >
            <div className='w-full items-end gap-1 text-start md:w-auto'>
              <Typography variant='body-S' className='flex gap-1 font-semibold'>
                {priceFrom ? t('from') : ''}
                <CurrencyLabel eventType={eventType} price={priceFrom} className='inline' />
              </Typography>
            </div>

            <Button
              onClick={onButtonClick}
              className='bg-primary-100 hover:bg-primary-100/90 flex w-full items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold transition-colors sm:text-[15px] md:w-auto'
            >
              <Typography className='text-body-XS sm:text-body-S flex items-center gap-1 text-nowrap font-semibold'>
                {buttonText || t('get_ticket')}
                <ChevronIcon className='size-2 sm:size-3 rtl:rotate-180' />
              </Typography>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventCard;
